import { LTSSService, Alert, FinancialSummary } from '../types/ltss';

const DB_NAME = 'ltss-analytics';
const DB_VERSION = 1;

export interface StoredReport {
  id: string;
  fileName: string;
  uploadDate: Date;
  weekRange: string;
  serviceCount: number;
  services: LTSSService[];
  alerts: Alert[];
  financials: FinancialSummary;
  summary: {
    totalRevenue: number;
    totalAlerts: number;
    criticalAlerts: number;
    staffCount: number;
    clientCount: number;
  };
}

export interface TrendDataPoint {
  date: string;
  weekRange: string;
  revenue: number;
  alerts: number;
  criticalAlerts: number;
  staffCount: number;
  clientCount: number;
  serviceCount: number;
}

let dbInstance: IDBDatabase | null = null;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (dbInstance) {
      resolve(dbInstance);
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create reports store
      if (!db.objectStoreNames.contains('reports')) {
        const reportStore = db.createObjectStore('reports', { keyPath: 'id' });
        reportStore.createIndex('uploadDate', 'uploadDate', { unique: false });
        reportStore.createIndex('weekRange', 'weekRange', { unique: false });
      }
    };
  });
}

export async function saveReport(report: StoredReport): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['reports'], 'readwrite');
    const store = transaction.objectStore('reports');
    const request = store.put(report);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getReport(id: string): Promise<StoredReport | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['reports'], 'readonly');
    const store = transaction.objectStore('reports');
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllReports(): Promise<StoredReport[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['reports'], 'readonly');
    const store = transaction.objectStore('reports');
    const request = store.getAll();
    
    request.onsuccess = () => {
      const reports = request.result || [];
      // Sort by upload date descending
      reports.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
      resolve(reports);
    };
    request.onerror = () => reject(request.error);
  });
}

export async function deleteReport(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['reports'], 'readwrite');
    const store = transaction.objectStore('reports');
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function clearAllReports(): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(['reports'], 'readwrite');
    const store = transaction.objectStore('reports');
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function getTrendData(): Promise<TrendDataPoint[]> {
  const reports = await getAllReports();
  
  return reports.map(report => ({
    date: new Date(report.uploadDate).toLocaleDateString(),
    weekRange: report.weekRange,
    revenue: report.summary.totalRevenue,
    alerts: report.summary.totalAlerts,
    criticalAlerts: report.summary.criticalAlerts,
    staffCount: report.summary.staffCount,
    clientCount: report.summary.clientCount,
    serviceCount: report.serviceCount,
  })).reverse(); // Oldest first for charts
}

export function generateReportId(): string {
  return `report-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Generate a deterministic ID based on report content to detect duplicates
export function generateContentBasedId(services: LTSSService[]): string {
  const weekRange = extractWeekRange(services);
  const serviceCount = services.length;
  
  // Get unique staff and client counts for fingerprinting
  const staffNames = new Set(services.map(s => s.staffName).filter(Boolean));
  const clientNames = new Set(services.map(s => s.clientName).filter(Boolean));
  
  // Create a hash-like string from the content
  const fingerprint = `${weekRange}|${serviceCount}|${staffNames.size}|${clientNames.size}`;
  
  // Simple hash function
  let hash = 0;
  for (let i = 0; i < fingerprint.length; i++) {
    const char = fingerprint.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return `report-${weekRange.replace(/[^a-zA-Z0-9]/g, '')}-${Math.abs(hash).toString(36)}`;
}

export function extractWeekRange(services: LTSSService[]): string {
  const weeks = Array.from(new Set(services.map(s => s.week).filter(Boolean)));
  if (weeks.length === 0) return 'Unknown';
  if (weeks.length === 1) return weeks[0];
  weeks.sort();
  return `${weeks[0]} - ${weeks[weeks.length - 1]}`;
}
